import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Input } from './input';

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
};

/**
 * Free-form tag input. Enter, comma, or blur commits the current value as a
 * new chip. Backspace on an empty input removes the last chip. Duplicates are
 * silently dropped.
 */
export function TagsInput({ value, onChange, placeholder = 'Type a tag and press Enter' }: Props) {
  const [draft, setDraft] = useState('');

  function commit(raw: string) {
    const tag = raw.trim();
    if (!tag) return;
    if (value.includes(tag)) {
      setDraft('');
      return;
    }
    onChange([...value, tag]);
    setDraft('');
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  function remove(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  return (
    <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 focus-within:border-[var(--color-ring)] focus-within:ring-2 focus-within:ring-[color-mix(in_oklab,var(--color-ring)_25%,transparent)]">
      {value.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded-md bg-[var(--color-surface-muted)] px-2 py-0.5 text-xs"
        >
          {tag}
          <button
            type="button"
            onClick={() => remove(tag)}
            aria-label={`Remove ${tag}`}
            className="inline-flex size-3.5 items-center justify-center rounded text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-foreground)]"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
        placeholder={value.length === 0 ? placeholder : ''}
        className="h-6 min-w-[8rem] flex-1 !border-0 !bg-transparent !p-0 !ring-0 focus:!ring-0"
      />
    </div>
  );
}
